import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Button, Checkbox, Input, Select } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import SearchIcon from "../../../assets/icons/SearchIcon";
import Avatar from "../../../assets/images/avatar.svg";
import {
  useGetAllDepartmentQuery,
  useGetSubDepartmentByIdGroupChatQuery,
} from "../../../features/superAdmin/employeeService/employeeManagementService";
import {
  useCreateGroupChatMutation,
  useGetChatUserListQuery,
} from "../../../features/superAdmin/ticketService/ticketServiceApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import CustomModal from "../../ui/CustomModal";

type CreateTeamModalProps = ModalComponentProps & {
  handleParentClose?: () => void;
};

export default function CreateTeamModal({
  handleClose,
  handleParentClose,
  ...rest
}: CreateTeamModalProps) {
  const [search, setSearch] = useState("");
  const [groupName, setGroupName] = useState("");
  const navigate = useNavigate();
  const [groupNameError, setGroupNameError] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    number | null
  >(null);
  const [selectedSubDepartmentId, setSelectedSubDepartmentId] = useState<
    number | null
  >(null);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const auth = useAppSelector((state) => state.auth);

  const {
    isError: allDepartmentIsError,
    error: allDepartmentError,
    data: allDepartmentData,
  } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100000000,
      status: "active",
    },
  });

  const id = selectedDepartmentId ? selectedDepartmentId : skipToken;

  const {
    isError: subDepartmentIsError,
    error: subDepartmentError,
    data: subDepartmentData,
  } = useGetSubDepartmentByIdGroupChatQuery(id);

  const {
    isError: getChatUserListIsError,
    error: getChatUserListError,
    data: getChatUserListData,
  } = useGetChatUserListQuery({
    params: {
      search,
      department_id: selectedDepartmentId ?? undefined,
      sub_department_id: selectedSubDepartmentId ?? undefined,
    },
  });

  const [
    createGroupChat,
    {
      isError: createGroupChatIsError,
      error: createGroupChatError,
      isSuccess: createGroupChatIsSuccess,
      isLoading: createGroupChatIsLoading,
      data: groupCreatedData,
    },
  ] = useCreateGroupChatMutation();

  useError(
    allDepartmentIsError,
    allDepartmentError,
    "Get all department list fail",
  );
  useError(
    subDepartmentIsError,
    subDepartmentError,
    "Get sub department list fail",
  );
  useError(
    getChatUserListIsError,
    getChatUserListError,
    "Get chat user list fail",
  );
  useError(
    createGroupChatIsError,
    createGroupChatError,
    "Group chat create fail",
  );

  const handleUserToggle = (id: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const allIds = getChatUserListData?.data?.map((user: any) => user.id) || [];
    if (selectedUserIds.length === allIds.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(allIds);
    }
  };

  const handleGroupChatCreate = () => {
    let hasError = false;

    if (!groupName.trim()) {
      setGroupNameError(true);
      hasError = true;
    } else {
      setGroupNameError(false);
    }

    if (selectedUserIds.length === 0) {
      toast("warning", "Please select at least one user to create the group.");
      hasError = true;
    }

    if (hasError) return;

    const data = {
      creator_id: auth?.user?.id,
      group_name: groupName,
      member_ids: selectedUserIds,
    };

    createGroupChat(data);
  };

  useEffect(() => {
    if (createGroupChatIsSuccess) {
      let basePath = "/user/communication/message";
      if (auth?.role === "Admin" || auth?.role === "super_admin") {
        basePath = "/admin/communication/message";
      }
      toast("success", "Group chat create successfully!");
      navigate(`${basePath}/group?chatId=${groupCreatedData?.data?.chatId}`);
      handleClose();
      handleParentClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createGroupChatIsSuccess, groupCreatedData?.data?.chatId]);

  return (
    <CustomModal
      title="Select group member"
      {...rest}
      handleClose={handleClose}
      width={360}
      footer={null}
    >
      <div>
        <Input
          prefix={<SearchIcon />}
          placeholder="Search"
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          allowClear
        />
        <div className="mt-3 flex gap-3">
          <Select
            showSearch
            placeholder="Department"
            optionFilterProp="label"
            options={allDepartmentData?.data.map((dep: any) => ({
              label: dep.name,
              value: dep.id,
            }))}
            onChange={(value) => setSelectedDepartmentId(value)}
            allowClear
            className="flex-1"
            value={selectedDepartmentId}
          />
          <Select
            showSearch
            placeholder="Sub-Department"
            optionFilterProp="label"
            options={subDepartmentData?.data.map((dep: any) => ({
              label: dep.name,
              value: dep.id,
            }))}
            onChange={(value) => setSelectedSubDepartmentId(value)}
            allowClear
            className="flex-1"
            value={selectedSubDepartmentId}
            disabled={!selectedDepartmentId}
          />
        </div>
        <div className="mt-2">
          <Input
            placeholder="Enter Group Name"
            value={groupName}
            onChange={(e) => {
              setGroupName(e.target.value);
              setGroupNameError(false);
            }}
            allowClear
            status={groupNameError ? "error" : ""}
          />
          {groupNameError && (
            <div className="text-red-500 text-sm mt-1">
              Group name is required.
            </div>
          )}
        </div>
        <div>
          <div className="flex justify-between items-center mt-2 py-2 mr-4">
            <span>Select all</span>
            <span>
              <Checkbox
                checked={
                  (getChatUserListData?.data?.length || 0) > 0 &&
                  selectedUserIds.length ===
                    (getChatUserListData?.data?.length || 0)
                }
                indeterminate={
                  selectedUserIds.length > 0 &&
                  selectedUserIds.length <
                    (getChatUserListData?.data?.length || 0)
                }
                onChange={handleSelectAll}
              />
            </span>
          </div>
          <div className="max-h-[300px] overflow-auto pr-2">
            {getChatUserListData?.data?.map((user: any) => (
              <div
                key={user?.id}
                className="flex items-center justify-between mb-2"
              >
                <div className="flex gap-4 items-center">
                  <div>
                    <img
                      className="w-10 h-10 rounded-full object-cover"
                      src={user?.personalInfo?.pro_pic || Avatar}
                      alt={user?.personalInfo?.name}
                    />
                  </div>
                  <div className="text-[17px]">{user?.personalInfo?.name}</div>
                </div>
                <div>
                  <Checkbox
                    checked={selectedUserIds.includes(user.id)}
                    onChange={() => handleUserToggle(user.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="py-2 flex justify-between">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            loading={createGroupChatIsLoading}
            onClick={handleGroupChatCreate}
            className="bg-[#FFF1EB] text-[#FE7235] border-none"
          >
            Next
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
